/**
 * Change Detail Page
 * View change request details with workflow actions
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Send, 
  TickCircle, 
  CloseCircle,
  Play,
  Timer1,
  RefreshCircle,
  Danger,
  DocumentText,
  Calendar,
  Clock,
  User,
  Warning2,
  ArrowRight2,
  Copy,
  MessageText,
  DocumentDownload
} from 'iconsax-react';
import { 
  getChange, 
  submitChange,
  approveChange,
  rejectChange,
  scheduleChange,
  startChange,
  completeChange,
  failChange,
  rollbackChange,
  closeChange,
  cancelChange,
  ChangeRequest,
  getStatusColor,
  getStatusLabel,
  getRiskColor,
  getCategoryIcon,
  getTypeColor,
  exportChangeToPDF,
  getApprovalStepRoleLabel
} from '../../services/changeManagementApi';
import { formatDateTimeIST } from '../../utils/timezone';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const ChangeDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  
  const [change, setChange] = useState<ChangeRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // Modal form data
  const [rejectReason, setRejectReason] = useState('');
  const [approveComment, setApproveComment] = useState('');
  const [scheduleStart, setScheduleStart] = useState('');
  const [scheduleEnd, setScheduleEnd] = useState('');
  const [implementationNotes, setImplementationNotes] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [lessonsLearned, setLessonsLearned] = useState('');
  const [failReason, setFailReason] = useState('');
  const [rollbackNotes, setRollbackNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (id) {
      fetchChange();
    }
  }, [id]);

  const fetchChange = async () => {
    setLoading(true);
    try {
      const data = await getChange(id!, true);
      setChange(data);
    } catch (error) {
      console.error('Error fetching change:', error);
      toast.error('Failed to load change');
      navigate('/changes');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: string, data?: any) => {
    if (!change) return;
    
    setActionLoading(true);
    try {
      let updatedChange: ChangeRequest;
      
      switch (action) {
        case 'submit':
          updatedChange = await submitChange(change.change_id);
          toast.success('Change submitted for approval');
          break;
        case 'approve':
          updatedChange = await approveChange(change.change_id, approveComment);
          toast.success('Change approved');
          setApproveComment('');
          break;
        case 'reject':
          if (!rejectReason.trim()) {
            toast.error('Rejection reason is required');
            setActionLoading(false);
            return;
          }
          updatedChange = await rejectChange(change.change_id, rejectReason);
          toast.success('Change rejected');
          setShowRejectModal(false);
          setRejectReason('');
          break;
        case 'schedule':
          if (!scheduleStart) {
            toast.error('Schedule start is required');
            setActionLoading(false);
            return;
          }
          updatedChange = await scheduleChange(change.change_id, {
            scheduled_start: scheduleStart,
            scheduled_end: scheduleEnd || undefined
          });
          toast.success('Change scheduled');
          setShowScheduleModal(false);
          break;
        case 'start':
          updatedChange = await startChange(change.change_id);
          toast.success('Implementation started');
          break;
        case 'complete':
          updatedChange = await completeChange(change.change_id, implementationNotes);
          toast.success('Change completed successfully');
          setShowCompleteModal(false);
          break;
        case 'fail':
          if (!failReason.trim()) {
            toast.error('Failure reason is required');
            setActionLoading(false);
            return;
          }
          updatedChange = await failChange(change.change_id, failReason);
          toast.success('Change marked as failed');
          setShowFailModal(false);
          setFailReason('');
          break;
        case 'rollback':
          updatedChange = await rollbackChange(change.change_id, rollbackNotes || '');
          toast.success('Rollback recorded');
          setShowRollbackModal(false);
          setRollbackNotes('');
          break;
        case 'close':
          updatedChange = await closeChange(change.change_id, {
            review_notes: reviewNotes,
            lessons_learned: lessonsLearned
          });
          toast.success('Change closed');
          setShowCloseModal(false);
          break;
        case 'cancel':
          if (!cancelReason.trim()) {
            toast.error('Cancellation reason is required');
            setActionLoading(false);
            return;
          }
          updatedChange = await cancelChange(change.change_id, cancelReason);
          toast.success('Change cancelled');
          setShowCancelModal(false);
          setCancelReason('');
          break;
        default:
          setActionLoading(false);
          return;
      }
      
      setChange(updatedChange);
    } catch (error: any) {
      console.error(`Error performing ${action}:`, error);
      toast.error(error.response?.data?.error || `Failed to ${action} change`);
    } finally {
      setActionLoading(false);
    }
  };

  const copyChangeId = () => {
    if (change) {
      navigator.clipboard.writeText(change.change_id);
      toast.success('Change ID copied');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!change) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-gray-400">Change not found</p>
        <button onClick={() => navigate('/changes')} className="mt-4 text-primary hover:underline">
          Back to Changes
        </button>
      </div>
    );
  }

  // Compare as strings to handle any type mismatches
  // Compare as strings to handle any type mismatches (ObjectId vs string, etc.)
  // Also trim whitespace and handle null/undefined cases
  const user_id = user?.id ? String(user.id).trim() : null;
  const requested_by = change?.requested_by ? String(change.requested_by).trim() : null;
  const isCreator = user_id && requested_by && user_id === requested_by;
  
  // Creator can edit until fully approved (draft, rejected, submitted, under_review)
  // Once approved, no one can edit
  const canEdit = isCreator && change && ['draft', 'rejected', 'submitted', 'under_review'].includes(change.status);
  const canSubmit = change.status === 'draft' && isCreator;
  // Use backend-provided can_approve/can_reject flags to ensure only authorized users see buttons
  const canApprove = change.can_approve === true;
  const canReject = change.can_reject === true;
  // Only the creator can schedule and start implementation
  const canSchedule = isCreator && change.status === 'approved';
  const canStart = isCreator && (change.status === 'approved' || change.status === 'scheduled');
  const canComplete = change.status === 'in_progress';
  const canFail = change.status === 'in_progress';
  const canRollback = change.status === 'failed' || change.status === 'in_progress';
  const canClose = ['completed', 'failed', 'rolled_back', 'cancelled'].includes(change.status);
  // Only the creator can cancel, and only while not in progress / completed / closed
  const canCancel = isCreator && !['in_progress', 'completed', 'closed'].includes(change.status);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/changes')}
            className="p-2 rounded-lg bg-background-card border border-border-dark hover:bg-background-hover transition-colors mt-1"
            aria-label="Back to Changes"
          >
            <ArrowLeft size={20} color="#9CA3AF" variant="Outline" />
          </button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 
                className="text-primary font-mono text-lg cursor-pointer hover:underline"
                onClick={copyChangeId}
                title="Click to copy"
              >
                {change.change_id}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${getStatusColor(change.status)}`}>
                {getStatusLabel(change.status)}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getTypeColor(change.change_type)}`}>
                {change.change_type}
              </span>
              {change.requires_management_approval && (
                <span className="px-2 py-1 rounded-full text-xs font-medium text-white bg-orange-500/20 text-orange-400 border border-orange-500/50">
                  Management Approval Required
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-white">{change.title}</h2>
            <p className="text-gray-400 mt-1">
              Created by {change.requester_name || 'Unknown'} • {change.team_name}
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportChangeToPDF(change)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
            title="Download PDF Report"
          >
            <DocumentDownload size={18} color="#FFFFFF" />
            <span>Download PDF</span>
          </button>
          {canEdit && (
            <button
              onClick={() => navigate(`/changes/${change.change_id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-card border border-border-dark text-gray-300 hover:border-primary transition-colors"
            >
              <Edit size={18} color="#FFFFFF" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Workflow Actions Bar */}
      <div className="bg-background-card rounded-xl p-4 border border-border-dark">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-medium">Available Actions</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {canSubmit && (
              <button
                onClick={() => handleAction('submit')}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                <Send size={18} color="#FFFFFF" />
                Submit for Approval
              </button>
            )}
            {canApprove && (
              <button
                onClick={() => handleAction('approve')}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                <TickCircle size={18} color="#FFFFFF" />
                Approve
              </button>
            )}
            {canReject && (
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                <CloseCircle size={18} color="#FFFFFF" />
                Reject
              </button>
            )}
            {canSchedule && (
              <button
                onClick={() => setShowScheduleModal(true)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors disabled:opacity-50"
              >
                <Calendar size={18} color="#FFFFFF" />
                Schedule
              </button>
            )}
            {canStart && (
              <button
                onClick={() => handleAction('start')}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                <Play size={18} color="#FFFFFF" />
                Start Implementation
              </button>
            )}
            {canComplete && (
              <>
                <button
                  onClick={() => setShowCompleteModal(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                >
                  <TickCircle size={18} color="#FFFFFF" />
                  Complete
                </button>
                <button
                  onClick={() => setShowFailModal(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  <Danger size={18} color="#FFFFFF" />
                  Mark Failed
                </button>
              </>
            )}
            {canRollback && (
              <button
                onClick={() => setShowRollbackModal(true)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-pink-500 text-white hover:bg-pink-600 transition-colors disabled:opacity-50"
              >
                <RefreshCircle size={18} color="#FFFFFF" />
                Rollback
              </button>
            )}
            {canClose && (
              <button
                onClick={() => setShowCloseModal(true)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-500 text-white hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                <TickCircle size={18} color="#FFFFFF" />
                Close
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              >
                <CloseCircle size={18} color="#FFFFFF" />
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-background-card rounded-xl p-6 border border-border-dark">
            <h3 className="text-lg font-semibold text-white mb-4">Description</h3>
            <p className="text-gray-300 whitespace-pre-wrap">{change.description}</p>
            
            {change.justification && (
              <div className="mt-4 pt-4 border-t border-border-dark">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Business Justification</h4>
                <p className="text-gray-300">{change.justification}</p>
              </div>
            )}
          </div>

          {/* Implementation Plan */}
          {change.implementation_plan && (
            <div className="bg-background-card rounded-xl p-6 border border-border-dark">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DocumentText size={20} color="#34D399" />
                Implementation Plan
              </h3>
              <pre className="text-gray-300 whitespace-pre-wrap font-mono text-sm bg-background-hover p-4 rounded-lg">
                {change.implementation_plan}
              </pre>
            </div>
          )}

          {/* Rollback Plan */}
          {change.rollback_plan && (
            <div className="bg-background-card rounded-xl p-6 border border-border-dark">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <RefreshCircle size={20} color="#FB923C" />
                Rollback Plan
              </h3>
              <pre className="text-gray-300 whitespace-pre-wrap font-mono text-sm bg-background-hover p-4 rounded-lg">
                {change.rollback_plan}
              </pre>
            </div>
          )}

          {/* Test Plan */}
          {change.test_plan && (
            <div className="bg-background-card rounded-xl p-6 border border-border-dark">
              <h3 className="text-lg font-semibold text-white mb-4">Test Plan</h3>
              <pre className="text-gray-300 whitespace-pre-wrap font-mono text-sm bg-background-hover p-4 rounded-lg">
                {change.test_plan}
              </pre>
            </div>
          )}

          {/* Approval Workflow */}
          {change.approval_workflow && change.approval_workflow.length > 0 && (
            <div className="bg-background-card rounded-xl p-6 border border-border-dark">
              <h3 className="text-lg font-semibold text-white mb-4">Approval Workflow</h3>
              <div className="space-y-3">
                {change.approval_workflow.map((step, index) => (
                  <div 
                    key={index}
                    className={`p-4 rounded-lg border ${
                      step.status === 'approved' ? 'border-green-500/30 bg-green-500/10' :
                      step.status === 'rejected' ? 'border-red-500/30 bg-red-500/10' :
                      step.status === 'pending' ? 'border-yellow-500/30 bg-yellow-500/10' :
                      'border-border-dark bg-background-hover'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          step.status === 'approved' ? 'bg-green-500' :
                          step.status === 'rejected' ? 'bg-red-500' :
                          step.status === 'pending' ? 'bg-yellow-500' :
                          'bg-gray-500'
                        }`}>
                          {step.status === 'approved' ? <TickCircle size={18} color="#FFFFFF" /> :
                           step.status === 'rejected' ? <CloseCircle size={18} color="#FFFFFF" /> :
                           <Timer1 size={18} color="#FFFFFF" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {step.approver_name ||
                              getApprovalStepRoleLabel(step.role, {
                                teamName: change?.team_name,
                                teamDepartment: change?.team_department,
                              })}
                          </p>
                          {step.role && (
                            <p className="text-gray-400 text-sm">
                              {getApprovalStepRoleLabel(step.role, {
                                teamName: change?.team_name,
                                teamDepartment: change?.team_department,
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm capitalize ${
                          step.status === 'approved' ? 'text-green-400' :
                          step.status === 'rejected' ? 'text-red-400' :
                          'text-yellow-400'
                        }`}>
                          {step.status}
                        </span>
                        {step.decided_at && (
                          <p className="text-gray-500 text-xs">
                            {formatDateTimeIST(step.decided_at)}
                          </p>
                        )}
                      </div>
                    </div>
                    {step.comments && (
                      <p className="mt-2 text-gray-300 text-sm pl-11">"{step.comments}"</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          {change.history && change.history.length > 0 && (
            <div className="bg-background-card rounded-xl p-6 border border-border-dark">
              <h3 className="text-lg font-semibold text-white mb-4">History</h3>
              <div className="space-y-4">
                {change.history.map((entry, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                      {index < change.history!.length - 1 && (
                        <div className="w-0.5 flex-1 bg-border-dark mt-2"></div>
                      )}
                    </div>
                    <div className="pb-4">
                      <p className="text-white font-medium capitalize">
                        {entry.action.replace(/_/g, ' ')}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {entry.performed_by_name || entry.performed_by} • {formatDateTimeIST(entry.timestamp)}
                      </p>
                      {entry.details && (
                        <p className="text-gray-500 text-sm mt-1">{entry.details}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Risk & Priority */}
          <div className="bg-background-card rounded-xl p-6 border border-border-dark">
            <h3 className="text-lg font-semibold text-white mb-4">Risk Assessment</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Risk Level</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${getRiskColor(change.risk_level)}`}>
                  {change.risk_level.charAt(0).toUpperCase() + change.risk_level.slice(1)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Risk Score</span>
                <span className="text-white font-medium">{change.risk_score}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Priority</span>
                <span className="text-white capitalize">{change.priority}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Impact</span>
                <span className="text-white capitalize">{change.impact}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Urgency</span>
                <span className="text-white capitalize">{change.urgency}</span>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="bg-background-card rounded-xl p-6 border border-border-dark">
            <h3 className="text-lg font-semibold text-white mb-4">Details</h3>
            <div className="space-y-4">
              <div>
                <span className="text-gray-400 text-sm">Category</span>
                <p className="text-white flex items-center gap-2 mt-1">
                  <span>{getCategoryIcon(change.category)}</span>
                  <span className="capitalize">{change.category}</span>
                </p>
              </div>
              {change.sub_category && (
                <div>
                  <span className="text-gray-400 text-sm">Sub-category</span>
                  <p className="text-white mt-1">{change.sub_category}</p>
                </div>
              )}
              <div>
                <span className="text-gray-400 text-sm">Team</span>
                <p className="text-white mt-1">{change.team_name || '-'}</p>
              </div>
              {change.regions && change.regions.length > 0 && (
                <div>
                  <span className="text-gray-400 text-sm">Regions</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {change.regions.map((region: string, idx: number) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 rounded text-xs font-medium bg-primary/20 text-primary border border-primary/30"
                      >
                        {region}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {change.assigned_to && (
                <div>
                  <span className="text-gray-400 text-sm">Assigned To</span>
                  <p className="text-white mt-1">{change.assignee_name || change.assigned_to}</p>
                </div>
              )}
              {change.depends_on_other_team && (
                <div className="md:col-span-2">
                  <span className="text-gray-400 text-sm">Depends On</span>
                  <p className="text-white mt-1">
                    {change.dependent_team_name || 'Another team'}
                    {change.dependent_member_name
                      ? ` → ${change.dependent_member_name}`
                      : ''}
                    {!change.assigned_to && change.dependent_member && (
                      <span className="ml-2 text-xs text-amber-400">
                        (will auto-assign after approval)
                      </span>
                    )}
                  </p>
                </div>
              )}
              {change.external_ticket && (
                <div>
                  <span className="text-gray-400 text-sm">External Ticket</span>
                  <p className="text-white mt-1">{change.external_ticket}</p>
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-background-card rounded-xl p-6 border border-border-dark">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-purple-400" />
              Schedule
            </h3>
            <div className="space-y-4">
              {change.scheduled_start && (
                <div>
                  <span className="text-gray-400 text-sm">Scheduled Start</span>
                  <p className="text-white mt-1">
                    {new Date(change.scheduled_start).toLocaleString()}
                  </p>
                </div>
              )}
              {change.scheduled_end && (
                <div>
                  <span className="text-gray-400 text-sm">Scheduled End</span>
                  <p className="text-white mt-1">
                    {new Date(change.scheduled_end).toLocaleString()}
                  </p>
                </div>
              )}
              {change.estimated_duration && (
                <div>
                  <span className="text-gray-400 text-sm">Estimated Duration</span>
                  <p className="text-white mt-1">{change.estimated_duration} minutes</p>
                </div>
              )}
              <div className="flex items-center gap-4">
                {change.maintenance_window && (
                  <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400 text-xs">
                    Maintenance Window
                  </span>
                )}
                {change.requires_downtime && (
                  <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs">
                    Requires Downtime
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Affected Items */}
          {(change.affected_systems?.length > 0 || change.affected_services?.length > 0) && (
            <div className="bg-background-card rounded-xl p-6 border border-border-dark">
              <h3 className="text-lg font-semibold text-white mb-4">Affected Items</h3>
              {change.affected_systems?.length > 0 && (
                <div className="mb-4">
                  <span className="text-gray-400 text-sm">Systems</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {change.affected_systems.map((system, i) => (
                      <span key={i} className="px-2 py-1 bg-background-hover rounded text-gray-300 text-sm">
                        {system}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {change.affected_services?.length > 0 && (
                <div>
                  <span className="text-gray-400 text-sm">Services</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {change.affected_services.map((service, i) => (
                      <span key={i} className="px-2 py-1 bg-background-hover rounded text-gray-300 text-sm">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-background-card rounded-xl p-6 border border-border-dark">
            <h3 className="text-lg font-semibold text-white mb-4">Timestamps</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Created</span>
                <span className="text-gray-300">
                  {change.created_at ? formatDateTimeIST(change.created_at) : '-'}
                </span>
              </div>
              {change.submitted_at && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Submitted</span>
                  <span className="text-gray-300">{formatDateTimeIST(change.submitted_at)}</span>
                </div>
              )}
              {change.approved_at && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Approved</span>
                  <span className="text-gray-300">{formatDateTimeIST(change.approved_at)}</span>
                </div>
              )}
              {change.actual_start && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Started</span>
                  <span className="text-gray-300">{formatDateTimeIST(change.actual_start)}</span>
                </div>
              )}
              {change.actual_end && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Ended</span>
                  <span className="text-gray-300">{formatDateTimeIST(change.actual_end)}</span>
                </div>
              )}
              {change.closed_at && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Closed</span>
                  <span className="text-gray-300">{formatDateTimeIST(change.closed_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-card rounded-xl p-6 w-full max-w-md border border-border-dark">
            <h3 className="text-xl font-semibold text-white mb-4">Reject Change</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction('reject')}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-card rounded-xl p-6 w-full max-w-md border border-border-dark">
            <h3 className="text-xl font-semibold text-white mb-4">Schedule Change</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Start Date/Time *</label>
                <input
                  type="datetime-local"
                  value={scheduleStart}
                  onChange={(e) => setScheduleStart(e.target.value)}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">End Date/Time</label>
                <input
                  type="datetime-local"
                  value={scheduleEnd}
                  onChange={(e) => setScheduleEnd(e.target.value)}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="px-4 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction('schedule')}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-card rounded-xl p-6 w-full max-w-md border border-border-dark">
            <h3 className="text-xl font-semibold text-white mb-4">Complete Change</h3>
            <textarea
              value={implementationNotes}
              onChange={(e) => setImplementationNotes(e.target.value)}
              placeholder="Implementation notes (optional)..."
              rows={4}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowCompleteModal(false)}
                className="px-4 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction('complete')}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-card rounded-xl p-6 w-full max-w-md border border-border-dark">
            <h3 className="text-xl font-semibold text-white mb-4">Close Change - Post Implementation Review</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Review Notes</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Notes from post-implementation review..."
                  rows={3}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Lessons Learned</label>
                <textarea
                  value={lessonsLearned}
                  onChange={(e) => setLessonsLearned(e.target.value)}
                  placeholder="What did we learn from this change?"
                  rows={3}
                  className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowCloseModal(false)}
                className="px-4 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction('close')}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-slate-500 text-white hover:bg-slate-600 disabled:opacity-50"
              >
                Close Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Failed Modal */}
      {showFailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-card rounded-xl p-6 w-full max-w-md border border-border-dark">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Danger size={24} color="#EF4444" />
              Mark Change as Failed
            </h3>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Failure Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={failReason}
                onChange={(e) => setFailReason(e.target.value)}
                placeholder="Enter the reason why this change failed..."
                rows={4}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowFailModal(false);
                  setFailReason('');
                }}
                className="px-4 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction('fail')}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                Mark Failed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rollback Modal */}
      {showRollbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-card rounded-xl p-6 w-full max-w-md border border-border-dark">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <RefreshCircle size={24} color="#EC4899" />
              Rollback Change
            </h3>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Rollback Notes</label>
              <textarea
                value={rollbackNotes}
                onChange={(e) => setRollbackNotes(e.target.value)}
                placeholder="Enter rollback notes (optional)..."
                rows={4}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRollbackModal(false);
                  setRollbackNotes('');
                }}
                className="px-4 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction('rollback')}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-pink-500 text-white hover:bg-pink-600 disabled:opacity-50"
              >
                Rollback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-card rounded-xl p-6 w-full max-w-md border border-border-dark">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <CloseCircle size={24} color="#EF4444" />
              Cancel Change Request
            </h3>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter the reason for cancelling this change request..."
                rows={4}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="px-4 py-2 rounded-lg border border-border-dark text-gray-300 hover:bg-background-hover"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction('cancel')}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                Cancel Change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeDetailPage;
